import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { showToast } from 'Core/redux/toast/slice';
import {
  QUALITY_STATUSES,
  QUALITY_STATUSES_COLORS,
  QUALITY_STATUS_ID,
} from 'Core/constants/constants';
import CustomPropTypes from 'Core/prop-types';
import {
  approveRejectInspectionResults,
  extendInspectionResults,
  fetchInspectionResultsData,
} from './store/slice';
import { AppModal } from '../components/app-modals';
import { useTransformOptions } from 'Core/hooks';
import QualityCategory from './inspection-overview';
import { Modal, Tabs, Tab, FooterButton } from 'Core/components';
import { selectParts } from 'Core/redux/part/selectors';
import CustomAccordion from '../../core/components/accordion/accordion';
import {
  selectFiIteredInspectionResultData,
  inspectionResultsDataSortKey,
  selectInspectionResultsDeepLink,
} from './store/selectors';
import { actions as InspectionResultsActions } from './store/slice';
import InspectionResultsSearch from './inspectionresults-search';
import InspectionOverview from './inspection-overview';
import InspectionAQLResults from './inspection-aql-results';
import InspectionQcResults from './inspection-qc-results';

const {
  setInspectionResultsSortKey,
  setInspectionResultsDataFilters,
  setInspectionResultsQCDataFilters,
  setQcResultsPagination,
  resetQcResultsPagination,
  setAqlResultsPagination,
  resetAqlResultsPagination,
  clearDeepLink,
} = InspectionResultsActions;

const initData = [
  {
    id: 0,
    partId: 0,
    type: '',
  },
];

const InspectionResultsAddEdit = ({
  showModal,
  selectedPart,
  modalData,
  onCloseModal,
  onChanges,
}) => {
  const InspectionResultsData = useSelector(selectFiIteredInspectionResultData);
  const deepLink = useSelector(selectInspectionResultsDeepLink);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  // const qualityApprovalData = modalData;
  const [formData, setFormData] = useState(
    InspectionResultsData ? InspectionResultsData : initData,
  );
  const [activeTab, setActiveTab] = useState(() => {
    if (deepLink?.tab === 'aql') return 1;
    if (deepLink?.tab === 'release') return 2;
    return 0;
  });

  const sortKey = useSelector(inspectionResultsDataSortKey);

  const [dataSortKey, setDataSortKey] = useState(sortKey);

  useEffect(() => {
    setFormData(InspectionResultsData);
    
    // Update QC pagination state when data changes - with fallback
    if (InspectionResultsData?.totalQCResultsCount !== undefined) {
      setQcPagination(prev => ({
        ...prev,
        totalCount: InspectionResultsData.totalQCResultsCount,
        totalPages: Math.ceil(InspectionResultsData.totalQCResultsCount / prev.pageSize),
      }));
    } else if (InspectionResultsData?.qcResults && Array.isArray(InspectionResultsData.qcResults)) {
      // Fallback: use qcResults length if totalQCResultsCount is not available
      setQcPagination(prev => ({
        ...prev,
        totalCount: InspectionResultsData.qcResults.length,
        totalPages: Math.ceil(InspectionResultsData.qcResults.length / prev.pageSize),
      }));
    }

    // Update AQL pagination state when data changes - with fallback
    if (InspectionResultsData?.totalAQLResultsCount !== undefined) {
      setAqlPagination(prev => ({
        ...prev,
        totalCount: InspectionResultsData.totalAQLResultsCount,
        totalPages: Math.ceil(InspectionResultsData.totalAQLResultsCount / prev.pageSize),
      }));
    } else if (InspectionResultsData?.aqlResults && Array.isArray(InspectionResultsData.aqlResults)) {
      // Fallback: use aqlResults length if totalAQLResultsCount is not available
      setAqlPagination(prev => ({
        ...prev,
        totalCount: InspectionResultsData.aqlResults.length,
        totalPages: Math.ceil(InspectionResultsData.aqlResults.length / prev.pageSize),
      }));
    }
  }, [InspectionResultsData]);

  useEffect(() => {
    if (deepLink?.tab === 'aql') {
      setActiveTab(1);
    } else if (deepLink?.tab === 'release') {
      setActiveTab(2);
    }
  }, [deepLink]);

  useEffect(() => {
    setDataSortKey(sortKey);
  }, [sortKey]);
  const parts = useSelector(selectParts);
  const partsOptions = useTransformOptions(parts, 'id', 'partNumber', '', 'Select Part');

  // Pagination state
  const [qcPagination, setQcPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalCount: 0,
    totalPages: 0,
  });

  const [aqlPagination, setAqlPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalCount: 0,
    totalPages: 0,
  });

  useEffect(() => {
    return () => {
      dispatch(setInspectionResultsDataFilters({ toDate: null, fromDate: null }));
      dispatch(setInspectionResultsQCDataFilters({ toDate: null, fromDate: null }));
      dispatch(resetQcResultsPagination());
    };
  }, []);
  const isEditMode = Boolean(InspectionResultsData?.id);

  const handleSort = val => {
    // const sortKey = val;
    setDataSortKey(val);
    dispatch(setInspectionResultsSortKey(val));
  };

  const handleQcPageChange = async (pageNumber, pageSize = qcPagination.pageSize) => {
    setQcPagination(prev => ({
      ...prev,
      currentPage: pageNumber,
      pageSize: pageSize,
    }));
    
    // Fetch new data with pagination
    if (selectedPart) {
      await dispatch(fetchInspectionResultsData({ 
        inspectionresults: selectedPart, 
        pageNumber, 
        pageSize 
      }));
    }
  };

  const handleAqlPageChange = async (pageNumber, pageSize = aqlPagination.pageSize) => {
    setAqlPagination(prev => ({
      ...prev,
      currentPage: pageNumber,
      pageSize: pageSize,
    }));
    
    // Fetch new data with pagination
    if (selectedPart) {
      await dispatch(fetchInspectionResultsData({ 
        inspectionresults: selectedPart, 
        pageNumber, 
        pageSize 
      }));
    }
  };

  const oldandlatestvalue = [
    { value: 'categoryName', label: 'Sort By Category' },
    { value: 'statusId', label: 'Sort By Status' },
  ];

  const onChangesTools = (action, { requestId, comments, statusChangedBy }) => {
    if (action) {
      const payload = {
        requestId,
        statusId: action,
        comments,
        statusChangedBy: localStorage.getItem('UserId'),
        statusChangedDate: new Date(),
        approvedBy: localStorage.getItem('UserName'),
        approvedDate: new Date(),
      };
      dispatch(approveRejectInspectionResults(payload)).then(res => {
        dispatch(fetchInspectionResultsData({ 
          inspectionresults: selectedPart, 
          pageNumber: qcPagination.currentPage, 
          pageSize: qcPagination.pageSize 
        }));
        // .then((approvalData) => setFormData(approvalData.payload));
        // console.log(res)
      });
    }
  };

  const updateField = (requestId, fieldName, value) => {
    setFormData(prevData => {
      const updatedData = prevData.map(qaObj => {
        return qaObj.requestId == requestId
          ? {
              ...qaObj,
              [fieldName]: value,
            }
          : qaObj;
      });
      return updatedData;
    });
  };

  const onCommentUpdate = (requestId, comments) => {
    updateField(requestId, 'comments', comments);
  };

  const handleClose = () => {
    dispatch(clearDeepLink());
    onCloseModal();
    const params = new URLSearchParams(location.search);
    if (params.has('partId') || params.has('resultId') || params.has('tab') || params.has('workTicket')) {
      navigate('/inspection-results', { replace: true });
    }
  };
  const onExtend = () => {
    const payload = {
      ...formData,
      savedAt: new Date().toISOString(),
    };
    dispatch(extendInspectionResults(payload));
  };

  const updateFormField = (fieldName, value) => {
    setFormData(prevData => ({
      ...prevData,
      [fieldName]: value,
    }));
    if (onChanges && typeof onChanges === 'function') {
      onChanges({
        ...formData,
        [fieldName]: value,
      });
    }
  };

  const inspectionTypes = [
    { value: 'AQL Inspection', label: 'AQL Inspection' },
    { value: 'Release Inspection', label: 'Release Inspection' },
  ];

  const statusName = statusId => {
    switch (statusId) {
      case QUALITY_STATUS_ID.CREATED:
        return QUALITY_STATUSES.CREATED;
      case QUALITY_STATUS_ID.APPROVED:
        return QUALITY_STATUSES.APPROVED;
      case QUALITY_STATUS_ID.REJECTED:
        return QUALITY_STATUSES.REJECTED;
      case QUALITY_STATUS_ID.CONDITIONALLY_APPROVED:
        return QUALITY_STATUSES.CONDITIONALLY_APPROVED;
      default:
        return '';
    }
  };

  const statusColor = statusId => {
    switch (statusId) {
      case QUALITY_STATUS_ID.CREATED:
        return QUALITY_STATUSES_COLORS.WARNING;
      case QUALITY_STATUS_ID.APPROVED:
        return QUALITY_STATUSES_COLORS.SUCCESS;
      case QUALITY_STATUS_ID.REJECTED:
        return QUALITY_STATUSES_COLORS.DANGER;
      case QUALITY_STATUS_ID.CONDITIONALLY_APPROVED:
        return QUALITY_STATUSES_COLORS.INFO;
      default:
        return QUALITY_STATUSES_COLORS.SECONDARY;
    }
  };

  const body = () => {
    return (
      <div className="row">
        <div className="col-12 px-lg-5">
          <Tabs className="mb-5" activeIndex={activeTab} onTabChange={setActiveTab}>
            <Tab className="d-flex d-lg-block flex-column align-items-center" title="Overview">
              <InspectionOverview data={formData} />
            </Tab>
            <Tab title="AQL Results">
              <InspectionAQLResults 
                data={formData.aqlResults} 
                selectedPart={selectedPart}
                pagination={aqlPagination}
                onPageChange={handleAqlPageChange}
                focusedResultId={deepLink?.tab === 'aql' ? deepLink.resultId : null}
                focusedWorkTicket={deepLink?.tab === 'aql' ? deepLink.workTicket : ''}
              />
            </Tab>
            <Tab title="Release Inspection Results">
              <InspectionQcResults 
                data={formData.qcResults} 
                selectedPart={selectedPart}
                pagination={qcPagination}
                onPageChange={handleQcPageChange}
                focusedResultId={deepLink?.tab === 'release' ? deepLink.resultId : null}
                focusedWorkTicket={deepLink?.tab === 'release' ? deepLink.workTicket : ''}
              />
            </Tab>
          </Tabs>
        </div>
      </div>
    );
  };

  const valid = () => {
    if (formData?.length === 0) {
      dispatch(
        showToast({
          message: 'Please fill all required fields',
          type: 'danger',
        }),
      );
      return false;
    }
    return true;
  };

  const footer = () => {
    return null;
  };

  return (
    <Modal
      id="InspectionResults-modal"
      show={showModal}
      headerStyle={{ minHeight: '85px' }}
      size="xl"
      title={
        <div className="d-flex flex-row align-items-center">
          <h4 className="mb-0">Inspection Result</h4>
          {formData?.partNumber && (
            <span className="ms-3 badge bg-primary fs-6">
              Part: {formData.partNumber}
            </span>
          )}
        </div>
      }
      body={body}
      footer={footer}
      handleClose={handleClose}
    />
  );
};

InspectionResultsAddEdit.propTypes = {
  showModal: PropTypes.bool,
  modalData: PropTypes.arrayOf(PropTypes.shape(CustomPropTypes.InspectionResults)),
  onCloseModal: PropTypes.func,
  onChanges: PropTypes.func,
  selectedPart: PropTypes.object,
};

export default AppModal(InspectionResultsAddEdit);
